import { IBrandSampleProgram } from '@faire/web-api--source/faire/samples/IBrandSampleProgram';
import { IBadge } from '@faire/web-api--source/indigofair/badges/IBadge';
import { FreeShippingReason } from '@faire/web-api--source/indigofair/data/FreeShippingReason';
import { IBrandPromoCollection } from '@faire/web-api--source/indigofair/data/IBrandPromoCollection';
import { IDisclaimer } from '@faire/web-api--source/indigofair/data/IDisclaimer';
import { IFaireIcon } from '@faire/web-api--source/indigofair/data/IFaireIcon';
import { IFaireMoney } from '@faire/web-api--source/indigofair/data/IFaireMoney';
import { IImage } from '@faire/web-api--source/indigofair/data/IImage';
import { IInformationMessage } from '@faire/web-api--source/indigofair/data/IInformationMessage';
import { IItemPromoPrice } from '@faire/web-api--source/indigofair/data/IItemPromoPrice';
import { IProduct } from '@faire/web-api--source/indigofair/data/IProduct';
import { IProductCustomization } from '@faire/web-api--source/indigofair/data/IProductCustomization';
import { IProductOption } from '@faire/web-api--source/indigofair/data/IProductOption';
import { IProductOptionMeasurements } from '@faire/web-api--source/indigofair/data/IProductOptionMeasurements';
import { IProductOrderabilityCounts } from '@faire/web-api--source/indigofair/data/IProductOrderabilityCounts';
import { IProductsResponse } from '@faire/web-api--source/indigofair/data/IProductsResponse';
import { IProductTesterPrice } from '@faire/web-api--source/indigofair/data/IProductTesterPrice';
import { IProductVariationMap } from '@faire/web-api--source/indigofair/data/IProductVariationMap';
import { ITaxonomyType } from '@faire/web-api--source/indigofair/data/ITaxonomyType';
import { IUserTranslatedString } from '@faire/web-api--source/indigofair/data/IUserTranslatedString';
import { IProductVideo } from '@faire/web-api--source/indigofair/video/IProductVideo';

export namespace IProductPageResponse {
  export namespace IPdpProduct {
    export namespace IVisuals {
      /**
       * Return an instance of IVisuals with the provided fields. Any field not
       * listed will be populated with an empty value (an empty array,
       * or undefined, whichever is valid for the type of the value.)
       */
      export const build = (partial?: Partial<IVisuals>): IVisuals =>
        ({
          badges: [],
          images: [],
          videos: [],
          ...(partial ?? {}),
        }) as IVisuals;
    }

    export interface IVisuals {
      badges: Array<IBadge>;
      images: Array<IImage>;
      videos: Array<IProductVideo>;
      show_vertical_image: boolean | undefined;
    }

    export namespace IPurchaseInformation {
      /**
       * Return an instance of IPurchaseInformation with the provided fields. Any field not
       * listed will be populated with an empty value (an empty array,
       * or undefined, whichever is valid for the type of the value.)
       */
      export const build = (
        partial?: Partial<IPurchaseInformation>
      ): IPurchaseInformation =>
        ({
          tester_prices: [],
          style_groups: {},
          customizations: [],
          ...(partial ?? {}),
        }) as IPurchaseInformation;
    }

    export interface IPurchaseInformation {
      order_by_date: number | undefined;
      unit_multiplier: number | undefined;
      tester_price: IFaireMoney | undefined;
      tester_promo_price: IItemPromoPrice | undefined;
      tester_prices: Array<IProductTesterPrice>;
      min_order_quantity: number | undefined;
      per_style_min_order_quantity: number | undefined;
      product_orderability_counts: IProductOrderabilityCounts | undefined;
      non_returnable_reason_data: IProduct.INonReturnableReasonData | undefined;
      style_groups: Partial<Record<string, IProduct.IOptionTokenList>>;
      variation_map: IProductVariationMap | undefined;
      customizations: Array<IProductCustomization>;
      is_high_sell_through: boolean | undefined;
      maker_best_seller: boolean | undefined;
      is_new: boolean | undefined;
      allow_sales_when_out_of_stock: boolean | undefined;
      import_shipment_info: string | undefined;
      is_mid_price_point_brand_apparel_product: boolean | undefined;
      max_option_retail_price: IFaireMoney | undefined;
      max_option_wholesale_price: IFaireMoney | undefined;
      has_pre_packs_available: boolean | undefined;
      is_preorderable: boolean | undefined;
    }

    export namespace IDetails {
      export namespace IProductInformationSubSection {
        export namespace ILink {
          /**
           * Return an instance of ILink with the provided fields. Any field not
           * listed will be populated with an empty value (an empty array,
           * or undefined, whichever is valid for the type of the value.)
           */
          export const build = (partial?: Partial<ILink>): ILink =>
            ({
              ...(partial ?? {}),
            }) as ILink;
        }

        export interface ILink {
          url: string | undefined;
          text: string | undefined;
        }

        export enum Type {
          TYPE_UNKNOWN = 'TYPE_UNKNOWN',
          DESCRIPTION = 'DESCRIPTION',
          DETAILS = 'DETAILS',
          SAFETY_INFORMATION = 'SAFETY_INFORMATION',
          ECO_FRIENDLY = 'ECO_FRIENDLY',
          INSIGHTS = 'INSIGHTS',
        }

        export namespace IUserTranslatedAttribute {
          /**
           * Return an instance of IUserTranslatedAttribute with the provided fields. Any field not
           * listed will be populated with an empty value (an empty array,
           * or undefined, whichever is valid for the type of the value.)
           */
          export const build = (
            partial?: Partial<IUserTranslatedAttribute>
          ): IUserTranslatedAttribute =>
            ({
              ...(partial ?? {}),
            }) as IUserTranslatedAttribute;
        }

        /**
         * Replacement to UserTranslatedString for attributes: delivers a separate name and value pair for attributes,
         * structured so client can properly parse and style attribute names and values.
         * UserTranslatedString previously stored attributes as concatenated "name :" + "value"
         */
        export interface IUserTranslatedAttribute {
          name: IUserTranslatedString | undefined;
          value: IUserTranslatedString | undefined;
        }

        export namespace IProductInformationSubSectionEntry {
          /**
           * Return an instance of IProductInformationSubSectionEntry with the provided fields. Any field not
           * listed will be populated with an empty value (an empty array,
           * or undefined, whichever is valid for the type of the value.)
           */
          export const build = (
            partial?: Partial<IProductInformationSubSectionEntry>
          ): IProductInformationSubSectionEntry =>
            ({
              ...(partial ?? {}),
            }) as IProductInformationSubSectionEntry;
        }

        export interface IProductInformationSubSectionEntry {
          /**
           * currently text is the default way to display pdp entries, client will use attribute to display the detail section
           */
          text: IUserTranslatedString | undefined;
          attribute:
            | IProductPageResponse.IPdpProduct.IDetails.IProductInformationSubSection.IUserTranslatedAttribute
            | undefined;
          /**
           * for the eco-friendly fields which use an icon instead of a bullet point
           * BE will add the bullet point for clients when it should be shown
           */
          slate_icon: IFaireIcon | undefined;
        }

        export namespace IOptionLevelEntries {
          /**
           * Return an instance of IOptionLevelEntries with the provided fields. Any field not
           * listed will be populated with an empty value (an empty array,
           * or undefined, whichever is valid for the type of the value.)
           */
          export const build = (
            partial?: Partial<IOptionLevelEntries>
          ): IOptionLevelEntries =>
            ({
              entries: [],
              ...(partial ?? {}),
            }) as IOptionLevelEntries;
        }

        export interface IOptionLevelEntries {
          entries: Array<IProductPageResponse.IPdpProduct.IDetails.IProductInformationSubSection.IProductInformationSubSectionEntry>;
        }

        export namespace IModalInformationSection {
          export namespace IAttributeValueDetail {
            /**
             * Return an instance of IAttributeValueDetail with the provided fields. Any field not
             * listed will be populated with an empty value (an empty array,
             * or undefined, whichever is valid for the type of the value.)
             */
            export const build = (
              partial?: Partial<IAttributeValueDetail>
            ): IAttributeValueDetail =>
              ({
                ...(partial ?? {}),
              }) as IAttributeValueDetail;
          }

          export interface IAttributeValueDetail {
            name: string | undefined;
            description: string | undefined;
          }

          /**
           * Return an instance of IModalInformationSection with the provided fields. Any field not
           * listed will be populated with an empty value (an empty array,
           * or undefined, whichever is valid for the type of the value.)
           */
          export const build = (
            partial?: Partial<IModalInformationSection>
          ): IModalInformationSection =>
            ({
              value_details: [],
              ...(partial ?? {}),
            }) as IModalInformationSection;
        }

        export interface IModalInformationSection {
          header_text: string | undefined;
          value_details: Array<IProductPageResponse.IPdpProduct.IDetails.IProductInformationSubSection.IModalInformationSection.IAttributeValueDetail>;
          slate_icon: IFaireIcon | undefined;
        }

        /**
         * Redacted copy of IProductInformationSubSection (i.e. without redacted fields).
         */
        export type Redacted = Omit<
          IProductInformationSubSection,
          'analytics_data'
        >;

        /**
         * Return an instance of IProductInformationSubSection with the provided fields. Any field not
         * listed will be populated with an empty value (an empty array,
         * or undefined, whichever is valid for the type of the value.)
         */
        export const build = (
          partial?: Partial<IProductInformationSubSection>
        ): IProductInformationSubSection =>
          ({
            entries: [],
            option_level_entries_by_option_token: {},
            modal_information_sections: [],
            ...(partial ?? {}),
          }) as IProductInformationSubSection;
      }

      export interface IProductInformationSubSection {
        /**
         * The name of the section to be displayed on the clients, such as "Details"
         */
        section_header_text: string | undefined;
        section_type: string | undefined;
        type:
          | keyof typeof IProductPageResponse.IPdpProduct.IDetails.IProductInformationSubSection.Type
          | undefined;
        entries: Array<IProductPageResponse.IPdpProduct.IDetails.IProductInformationSubSection.IProductInformationSubSectionEntry>;
        /**
         * Some section entries are product option specific (i.e. measurement entries in Details section)
         * we should append the entries in this map if populated
         * The key type is Token<PRODUCT_OPTION>
         */
        option_level_entries_by_option_token: Partial<
          Record<
            string,
            IProductPageResponse.IPdpProduct.IDetails.IProductInformationSubSection.IOptionLevelEntries
          >
        >;
        /**
         * These 3 fields are eco-friendly specific
         */
        modal_information_sections: Array<IProductPageResponse.IPdpProduct.IDetails.IProductInformationSubSection.IModalInformationSection>;
        /**
         * @deprecated
         */
        c2_link:
          | IProductPageResponse.IPdpProduct.IDetails.IProductInformationSubSection.ILink
          | undefined;
        external_link:
          | IProductPageResponse.IPdpProduct.IDetails.IProductInformationSubSection.ILink
          | undefined;
        /**
         * Randomly generated ID used to join PETE view events with backend analytics data for this section.
         */
        analytics_id: string | undefined;
        /**
         * JSON string containing analytics data for this section.
         * @protobufOption (indigofair.data.redacted) = true
         */
        analytics_data: string | undefined; // redacted
      }

      export namespace IProductInformationSectionGroup {
        /**
         * Return an instance of IProductInformationSectionGroup with the provided fields. Any field not
         * listed will be populated with an empty value (an empty array,
         * or undefined, whichever is valid for the type of the value.)
         */
        export const build = (
          partial?: Partial<IProductInformationSectionGroup>
        ): IProductInformationSectionGroup =>
          ({
            sections: [],
            ...(partial ?? {}),
          }) as IProductInformationSectionGroup;
      }

      export interface IProductInformationSectionGroup {
        sections: Array<IProductPageResponse.IPdpProduct.IDetails.IProductInformationSubSection>;
        /**
         * Specifies max number of lines in the group should use before truncating and showing a "see more" button
         */
        max_preview_lines: number | undefined;
        type: string | undefined;
      }

      /**
       * Return an instance of IDetails with the provided fields. Any field not
       * listed will be populated with an empty value (an empty array,
       * or undefined, whichever is valid for the type of the value.)
       */
      export const build = (partial?: Partial<IDetails>): IDetails =>
        ({
          attribute_tags: [],
          product_option_measurements: {},
          product_information_section_groups: [],
          ...(partial ?? {}),
        }) as IDetails;
    }

    export interface IDetails {
      taxonomy_type: ITaxonomyType | undefined;
      attribute_tags: Array<IProductsResponse.IProductAttributeTag>;
      /**
       * @deprecated
       */
      product_option_measurements: Partial<
        Record<string, IProductOptionMeasurements>
      >;
      description: IUserTranslatedString | undefined;
      /**
       * Made in information is included in productInformationSectionGroups
       * @deprecated
       */
      made_in: IUserTranslatedString | undefined;
      product_information_section_groups: Array<IProductPageResponse.IPdpProduct.IDetails.IProductInformationSectionGroup>;
    }

    export namespace IMessages {
      /**
       * Return an instance of IMessages with the provided fields. Any field not
       * listed will be populated with an empty value (an empty array,
       * or undefined, whichever is valid for the type of the value.)
       */
      export const build = (partial?: Partial<IMessages>): IMessages =>
        ({
          info_texts: [],
          disclaimers: [],
          ...(partial ?? {}),
        }) as IMessages;
    }

    export interface IMessages {
      info_texts: Array<IInformationMessage>;
      disclaimers: Array<IDisclaimer>;
    }

    export namespace IShippingInformation {
      /**
       * Return an instance of IShippingInformation with the provided fields. Any field not
       * listed will be populated with an empty value (an empty array,
       * or undefined, whichever is valid for the type of the value.)
       */
      export const build = (
        partial?: Partial<IShippingInformation>
      ): IShippingInformation =>
        ({
          ...(partial ?? {}),
        }) as IShippingInformation;
    }

    export interface IShippingInformation {
      expected_ship_date: number | undefined;
      expected_ship_window_days: number | undefined;
      has_mixed_ship_dates: boolean | undefined;
      shipping_section_header_text: string | undefined;
    }

    /**
     * Return an instance of IPdpProduct with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (partial?: Partial<IPdpProduct>): IPdpProduct =>
      ({
        options: {},
        board_tokens: [],
        promoted_by: [],
        ...(partial ?? {}),
      }) as IPdpProduct;
  }

  export interface IPdpProduct {
    /**
     * @protobufOption (.faire.common.tokenType) = PRODUCT
     */
    token: string | undefined;
    name: IUserTranslatedString | undefined;
    /**
     * map of optionToken to option
     */
    options: Partial<Record<string, IProductOption>>;
    state: keyof typeof IProduct.State | undefined;
    /**
     * @protobufOption (.faire.common.tokenType) = RETAILER_BOARD
     */
    board_tokens: Array<string>;
    /**
     * @deprecated
     */
    is_food_and_beverage: boolean | undefined;
    brand_sample_program: IBrandSampleProgram | undefined;
    visuals: IProductPageResponse.IPdpProduct.IVisuals | undefined;
    purchase_information:
      | IProductPageResponse.IPdpProduct.IPurchaseInformation
      | undefined;
    details: IProductPageResponse.IPdpProduct.IDetails | undefined;
    messages: IProductPageResponse.IPdpProduct.IMessages | undefined;
    shipping_information:
      | IProductPageResponse.IPdpProduct.IShippingInformation
      | undefined;
    is_promoted: boolean | undefined;
    promoted_by: Array<keyof typeof IProduct.PromotedType>;
    is_highlighted_product_for_promotional_event: boolean | undefined;
  }

  export namespace IPdpBrand {
    /**
     * Return an instance of IPdpBrand with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (partial?: Partial<IPdpBrand>): IPdpBrand =>
      ({
        active_promo_collections: [],
        ...(partial ?? {}),
      }) as IPdpBrand;
  }

  export interface IPdpBrand {
    /**
     * @protobufOption (.faire.common.tokenType) = BRAND
     */
    token: string | undefined;
    name: string | undefined;
    minimum_order_amount: IFaireMoney | undefined;
    /**
     * @deprecated
     */
    free_shipping_reason: keyof typeof FreeShippingReason | undefined;
    profile_image: IImage | undefined;
    squared_image: IImage | undefined;
    published_products_count: number | undefined;
    /**
     * @deprecated
     */
    lower_bound_lead_time_days: number | undefined;
    /**
     * @deprecated
     */
    upper_bound_lead_time_days: number | undefined;
    /**
     * @deprecated
     */
    shipping_time_lower_bound_days: number | undefined;
    /**
     * @deprecated
     */
    shipping_time_upper_bound_days: number | undefined;
    /**
     * This replaces client-side calculations. It accounts for holidays + weekends
     */
    estimated_get_by_date_range_formatted: string | undefined;
    is_top_shop: boolean | undefined;
    active: boolean | undefined;
    is_apparel: boolean | undefined;
    hide_chat: boolean | undefined;
    active_promo_collections: Array<IBrandPromoCollection>;
    lead_time_days: number | undefined;
    forbids_online_only_retailers: boolean | undefined;
    brand_size_chart: IImage | undefined;
    vacation_start_date: number | undefined;
    vacation_end_date: number | undefined;
    owner_first_name: string | undefined;
    /**
     * @protobufOption (.faire.common.tokenType) = USER
     */
    owner_user_token: string | undefined;
    average_rating: string | undefined;
    number_of_reviews: number | undefined;
    has_volume_pricing: boolean | undefined;
  }

  /**
   * Return an instance of IProductPageResponse with the provided fields. Any field not
   * listed will be populated with an empty value (an empty array,
   * or undefined, whichever is valid for the type of the value.)
   */
  export const build = (
    partial?: Partial<IProductPageResponse>
  ): IProductPageResponse =>
    ({
      settings_to_assign_on_page_view: [],
      ...(partial ?? {}),
    }) as IProductPageResponse;
}

export interface IProductPageResponse {
  product: IProductPageResponse.IPdpProduct | undefined;
  brand: IProductPageResponse.IPdpBrand | undefined;
  settings_to_assign_on_page_view: Array<string>;
}
